import { Card, DatePicker, Form, Select, Skeleton, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-google-charts";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { columnChartConfigurationOptions } from "../../config";
import HTTP from "../../services/httpService";
import { disableFutureDates, formatDate } from "../../services/util";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const chartByOption = [
    { key: "year", title: "Year" },
    { key: "days", title: "Day" },
];
function LinesAddedChart({ projects, users }) {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [yearSelected, setYearSelected] = useState(moment());
    const [dateRangeSelected, setDateRangeSelected] = useState([
        moment().subtract(7, "day"),
        moment(),
    ]);
    const [form] = Form.useForm();
    const [gitCommits, setGitCommits] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        range_year: yearSelected.get("year"),
        project_id: -1,
        user_id: "",
        chart_by: chartByOption[0].key,
    });
    const [loader, setLoader] = useState(true);

    const {
        status: gitCommitStatus,
        data: gitCommitRes,
        refetch: getGitCommits,
    } = useQuery(
        "gitCommitData",
        async () => {
            return await HTTP("/analytics/getGitCommitsMonthlyData", {
                ...formData,
            });
        },
        { enabled: false },
    );

    useEffect(() => {
        getGitCommits();
        form.setFieldsValue({
            chart_by: chartByOption[0].key,
            range_year: yearSelected,
        });
    }, []);

    useEffect(() => {
        setLoader(true);
        getGitCommits();
    }, [formData]);

    useEffect(() => {
        if (gitCommitStatus === "success") {
            setLoader(false);
            setGitCommits(gitCommitRes.data.data);
        }
    }, [gitCommitStatus, gitCommitRes]);

    function handleYearChange(date, dateString) {
        const yearSelected = dateString || moment().get("year");
        setYearSelected(dateString === "" ? moment() : moment(dateString));
        const updatedFormData = {
            ...formData,
            range_year: yearSelected,
        };
        setFormData(updatedFormData);
    }

    function handleProjectSelectionChange(e) {
        let project_id = -1;
        if (e !== undefined) {
            [project_id] = e.split("#");
        }

        const updatedFormData = {
            ...formData,
            project_id,
        };
        setFormData(updatedFormData);
    }

    function handleUserSelectionChange(user_id) {
        const updatedFormData = {
            ...formData,
            user_id,
        };
        setFormData(updatedFormData);
    }

    function handleChartBySelection(chart_by) {
        const updatedFormData = {
            ...formData,
            chart_by,
        };
        if (chart_by == "year") {
            updatedFormData.range_year = yearSelected.get("year");
            delete updatedFormData["start_date"];
            delete updatedFormData["end_date"];
            form.setFieldsValue({
                range_year: yearSelected,
            });
        } else {
            delete updatedFormData["range_year"];
            const [start_date, end_date] = dateRangeSelected;
            updatedFormData.start_date = formatDate(start_date);
            updatedFormData.end_date = formatDate(end_date);
            form.setFieldsValue({
                range_date: dateRangeSelected,
            });
        }
        setFormData(updatedFormData);
    }

    function handleDateRangeChange(e) {
        let start_date =
            moment(e[0]) < moment()
                ? moment(e[0]).startOf("day")
                : moment().startOf("day");
        let end_date =
            moment(e[1]) < moment()
                ? moment(e[1]).startOf("day")
                : moment().startOf("day");
        setDateRangeSelected([start_date, end_date]);

        form.setFieldsValue({
            range_date: [start_date, end_date],
        });
        const updatedFormData = {
            ...formData,
        };

        updatedFormData.start_date = formatDate(start_date);
        updatedFormData.end_date = formatDate(end_date);
        setFormData(updatedFormData);
    }

    function disabledDate(current) {
        return (
            (current && current < moment().subtract(90, "day")) ||
            current > moment().endOf("day")
        );
    }

    return (
        <Card className="box-shadow margin-10">
            <Form
                form={form}
                labelCol={{
                    span: 8,
                }}
                wrapperCol={{
                    span: 16,
                }}
            >
                <Form.Item label="Project" name="project_id">
                    <Select
                        id="project_id"
                        allowClear
                        showSearch
                        placeholder="Project"
                        loading={projects.length === 0 ? true : false}
                        onChange={handleProjectSelectionChange}
                        style={{ marginLeft: "0px" }}
                    >
                        {projects.map((e) => (
                            <Option key={`${e.project_id}#${e.project_name}`}>
                                {e.project_name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="User" name="user_id">
                    <Select
                        id="user_id"
                        allowClear
                        showSearch
                        mode="multiple"
                        placeholder="User"
                        loading={users.length === 0 ? true : false}
                        style={{ width: "20%" }}
                        onChange={handleUserSelectionChange}
                    >
                        {users.map((e) => (
                            <Option key={`${e.committer_email}`}>
                                {e.committer_name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Chart By" name="chart_by">
                    <Select id="chart_by" onChange={handleChartBySelection}>
                        {chartByOption.map((e) => (
                            <Option key={e.key} value={e.key}>
                                {e.title}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.chart_by !== currentValues.chart_by
                    }
                >
                    {({ getFieldValue }) =>
                        getFieldValue("chart_by") == "year" ? (
                            <Form.Item
                                label="Year"
                                name="range_year"
                                labelCol={{
                                    span: 12,
                                }}
                                wrapperCol={{
                                    span: 8,
                                }}
                            >
                                <DatePicker
                                    id="range_year"
                                    onChange={handleYearChange}
                                    picker="year"
                                    allowClear={false}
                                    disabledDate={disableFutureDates}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label="Date"
                                name="range_date"
                                labelCol={{
                                    span: 12,
                                }}
                                wrapperCol={{
                                    span: 8,
                                }}
                            >
                                <RangePicker
                                    disabledDate={disabledDate}
                                    id="range_date"
                                    onChange={handleDateRangeChange}
                                    format="MM/DD/YYYY"
                                    allowClear={false}
                                    ranges={{
                                        Today: [moment(), moment()],
                                        "Current Month": [
                                            moment().startOf("month"),
                                            moment(),
                                        ],
                                        "Last Month": [
                                            moment()
                                                .subtract(1, "month")
                                                .startOf("month"),
                                            moment()
                                                .subtract(1, "month")
                                                .endOf("month"),
                                        ],
                                    }}
                                />
                            </Form.Item>
                        )
                    }
                </Form.Item>
            </Form>
            {!loader ? (
                <>
                    <Chart
                        className="charts"
                        chartType="LineChart"
                        height="100vh"
                        data={gitCommits}
                        options={{
                            ...columnChartConfigurationOptions,
                            title: "Lines Changed",
                            isStacked: true,
                            curveType: "function",
                            vAxes: {
                                0: { title: "Lines" },
                                1: { title: "Merged Requests" },
                            },
                            legend: { position: "none" },
                            series: {
                                0: {
                                    hidden: false,
                                    color: "green",
                                    altColor: "transparent",
                                    lineWidth: 2,
                                    isVisible: true,
                                    targetAxisIndex: 0,
                                },
                                1: {
                                    hidden: false,
                                    color: "red",
                                    altColor: "transparent",
                                    lineWidth: 2,
                                    isVisible: true,
                                    targetAxisIndex: 0,
                                },
                                2: {
                                    hidden: false,
                                    color: "blue",
                                    altColor: "transparent",
                                    lineWidth: 2,
                                    isVisible: true,
                                    targetAxisIndex: 1,
                                },
                            },
                        }}
                    />
                    {formData.chart_by === "days" && (
                        <div className="display-flex flex-space-around margin-top-20">
                            <Text
                                code
                            >{`Commited Lines : ${gitCommitRes.data?.commit_lines}`}</Text>
                            <Text
                                code
                            >{`Deleted Lines : ${gitCommitRes.data?.deleted_lines}`}</Text>
                        </div>
                    )}
                </>
            ) : (
                <Skeleton />
            )}
        </Card>
    );
}

export default LinesAddedChart;
